/** Needs you, Today and Earlier, and the All chip's group, as hub sections (spec §3, §5). */
import type { NotificationRowData, PendingApproval } from "@/api/notifications";
import { rankHubResults } from "@/components/panels/search-modal/hub-ranking";
import type {
  HubItem,
  HubSection,
} from "@/components/panels/search-modal/hub-types";
import { approvalWords, notificationRowWords } from "./notification-words";
import { translate } from "@/i18n";
import { panelsNotificationsMessages } from "@/i18n/messages/panels-notifications";

export interface NotificationSources {
  approvals: readonly PendingApproval[];
  rows: readonly NotificationRowData[];
}

function startOfLocalDay(now: number): number {
  const date = new Date(now);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
}

function approvalItems(
  approvals: readonly PendingApproval[],
  query: string,
): HubItem[] {
  return rankHubResults(
    approvals.map((approval) => ({
      approval,
      ...approvalWords(approval),
      time: Date.parse(approval.created_at),
    })),
    query,
  ).map(({ approval }) => ({
    type: "approval",
    key: `approval-${approval.id}`,
    approval,
  }));
}

function rowItems(
  rows: readonly NotificationRowData[],
  query: string,
  projectId: string | null,
): HubItem[] {
  const scoped = projectId
    ? rows.filter(
        (row) =>
          row.project_id === projectId || row.target?.type === "release_notes",
      )
    : rows;
  return rankHubResults(
    scoped.map((row) => ({
      row,
      ...notificationRowWords(row),
      time: Date.parse(row.latest_at),
    })),
    query,
  ).map(({ row }) => ({
    type: "notification",
    key: `notification-${row.id}`,
    row,
  }));
}

/** Every pending approval from every project; the project filter never hides them. */
export function needsYouSection(
  approvals: readonly PendingApproval[],
  query: string,
): HubSection | null {
  const items = approvalItems(approvals, query);
  return items.length > 0
    ? {
        id: "needs-you",
        title: translate(panelsNotificationsMessages, "needsYou"),
        items,
      }
    : null;
}

/** The Notifications chip: Needs you, then Today and Earlier; the list actions sit on the first of those. */
export function notificationSections(
  sources: NotificationSources,
  {
    query,
    projectId,
    now,
  }: { query: string; projectId: string | null; now: number },
): HubSection[] {
  const needsYou = needsYouSection(sources.approvals, query);
  const items = rowItems(sources.rows, query, projectId);
  const today = startOfLocalDay(now);
  const isToday = (item: HubItem) =>
    item.type === "notification" && Date.parse(item.row.latest_at) >= today;
  const plain: HubSection[] = [
    {
      id: "today",
      title: translate(panelsNotificationsMessages, "today"),
      items: items.filter(isToday),
    },
    {
      id: "earlier",
      title: translate(panelsNotificationsMessages, "earlier"),
      items: items.filter((item) => !isToday(item)),
    },
  ].filter((section) => section.items.length > 0);
  if (plain[0]) plain[0] = { ...plain[0], notificationActions: true };
  return needsYou ? [needsYou, ...plain] : plain;
}

/** The All chip's Notifications group while typing: matching approvals, then matching rows. */
export function notificationGroupItems(
  sources: NotificationSources,
  { query, projectId }: { query: string; projectId: string | null },
): HubItem[] {
  return [
    ...approvalItems(sources.approvals, query),
    ...rowItems(sources.rows, query, projectId),
  ];
}
